import React from "react";
import { Dialog, DialogContent, DialogTitle, DialogActions } from "@material-ui/core";

import Button from "../../../../../../components/common/PL/Button/Button";
import Select from "../../../../../../components/common/PL/Select/Select";
import { TransportCommunicationMethod } from "../../../../../../passapp-sdk/types/transportTypes";
import { TransportFlow } from "./transportAuthorizationTypes";

import "./RegistrarAutorizacionModal.css";

const OBSERVACIONES_MAX_LENGTH = 1000;

/** Etiquetas del medio de comunicación. Se exportan porque la caja verde de la autorización
 * aprobada por el guardia muestra exactamente lo mismo. */
export const COMMUNICATION_METHOD_LABELS: Record<TransportCommunicationMethod, string> = {
  wpp: "Whatsapp",
  sms: "Mensaje SMS",
  email: "Email",
  call: "Llamada telefónica",
  f2f: "Presencial",
  other: "Otros",
};

const COMMUNICATION_METHOD_OPTIONS = (
  Object.keys(COMMUNICATION_METHOD_LABELS) as TransportCommunicationMethod[]
).map((value) => ({ value, label: COMMUNICATION_METHOD_LABELS[value] }));

export type RegistrarAutorizacionSubmitData = {
  communicationMethod: TransportCommunicationMethod;
  authorizedBy: string;
  observations: string;
  /** Sólo se envía en entrada; el backend lo ignora en egreso. */
  dock?: string;
};

type RegistrarAutorizacionModalProps = {
  open: boolean;
  onClose: () => void;
  /** Entrada pide dock; salida no. */
  variant: TransportFlow;
  /** Observación del alta original, como referencia de sólo lectura. Sólo aplica cuando se está
   * aprobando una solicitud que ya existe: la nueva observación pisa a ésta. */
  previousObservations?: string | null;
  /** Punto de partida editable del textarea (p. ej. lo que el guardia ya escribió en el modal de
   * solicitud desde el que se abrió este). */
  prefillObservations?: string;
  onSubmit: (data: RegistrarAutorizacionSubmitData) => Promise<boolean>;
};

const RegistrarAutorizacionModal: React.FC<RegistrarAutorizacionModalProps> = ({
  open,
  onClose,
  variant,
  previousObservations,
  prefillObservations,
  onSubmit,
}) => {
  const [dock, setDock] = React.useState<string>("");
  const [communicationMethod, setCommunicationMethod] = React.useState<
    TransportCommunicationMethod | ""
  >("");
  const [authorizedBy, setAuthorizedBy] = React.useState<string>("");
  const [observaciones, setObservaciones] = React.useState<string>("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const isEntrada = variant === "entrada";

  const resetState = () => {
    setDock("");
    setCommunicationMethod("");
    setAuthorizedBy("");
    setObservaciones("");
  };

  // Al abrir se arranca de cero, salvo la observación: si viene de un modal de solicitud que ya
  // tenía uno cargado, se reusa como punto de partida editable.
  React.useEffect(() => {
    if (!open) return;
    setDock("");
    setCommunicationMethod("");
    setAuthorizedBy("");
    setObservaciones(prefillObservations ?? "");
  }, [open, prefillObservations]);

  // Obligatorios salvo la observación; el dock sólo cuando la autorización es de acceso.
  const isValid =
    !!communicationMethod &&
    authorizedBy.trim().length > 0 &&
    (!isEntrada || dock.trim().length > 0);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!isValid || isSubmitting || !communicationMethod) return;
    setIsSubmitting(true);
    const success = await onSubmit({
      communicationMethod,
      authorizedBy: authorizedBy.trim(),
      observations: observaciones.trim(),
      ...(isEntrada ? { dock: dock.trim() } : {}),
    });
    setIsSubmitting(false);
    if (success) {
      resetState();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        className: "registrarAutorizacionModalCard",
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle disableTypography className="registrarAutorizacionModalTitle">
        Detalles de la autorización
      </DialogTitle>
      <DialogContent className="registrarAutorizacionModalContent">
        {isEntrada && (
          <div className="registrarAutorizacionModalSection">
            <span className="registrarAutorizacionModalSectionLabel">Dock asignado *</span>
            <input
              type="text"
              className="registrarAutorizacionModalTextInput"
              value={dock}
              onChange={(e) => setDock(e.target.value)}
              placeholder="-completar-"
              disabled={isSubmitting}
            />
          </div>
        )}

        <div className="registrarAutorizacionModalSection">
          <Select
            value={communicationMethod}
            onChange={(value) => setCommunicationMethod(value as TransportCommunicationMethod)}
            options={COMMUNICATION_METHOD_OPTIONS}
            label="Medio de comunicación *"
            placeholder="-completar-"
            disabled={isSubmitting}
            className="registrarAutorizacionModalSelect"
            focusFirstOptionOnOpen
          />
        </div>

        <div className="registrarAutorizacionModalSection">
          <span className="registrarAutorizacionModalSectionLabel">
            Responsable que autorizó el {isEntrada ? "acceso" : "egreso"} *
          </span>
          <input
            type="text"
            className="registrarAutorizacionModalTextInput"
            value={authorizedBy}
            onChange={(e) => setAuthorizedBy(e.target.value)}
            placeholder="-completar-"
            disabled={isSubmitting}
          />
        </div>

        {previousObservations && (
          <div className="registrarAutorizacionModalSection">
            <span className="registrarAutorizacionModalSectionLabel">Observación original</span>
            <div className="registrarAutorizacionModalReadOnlyValue">{previousObservations}</div>
          </div>
        )}

        <div className="registrarAutorizacionModalSection">
          <span className="registrarAutorizacionModalSectionLabel">Observaciones</span>
          <textarea
            className="registrarAutorizacionModalTextArea"
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value.slice(0, OBSERVACIONES_MAX_LENGTH))}
            placeholder="-completar-"
            rows={3}
            disabled={isSubmitting}
          />
          {previousObservations && (
            <span className="registrarAutorizacionModalHelper">
              Reemplaza la observación original de la solicitud.
            </span>
          )}
          <span className="registrarAutorizacionModalCounter">
            {observaciones.length}/{OBSERVACIONES_MAX_LENGTH}
          </span>
        </div>
      </DialogContent>
      <DialogActions className="registrarAutorizacionModalActions">
        <Button
          variant="outline"
          onClick={handleClose}
          className="registrarAutorizacionModalButton"
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          variant="neutral"
          onClick={handleSubmit}
          className="registrarAutorizacionModalButton"
          disabled={!isValid || isSubmitting}
          loading={isSubmitting}
        >
          Aceptar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegistrarAutorizacionModal;
